import moment from 'moment';

import { IEmployeePresence, IEmployeesList, PresenceStatus } from '../models/types';

export const PRESENCE_STATUS_LABEL: Record<PresenceStatus, string> = {
  on_plan: 'На плане',
  online: 'В сети',
  offline: 'Не в сети'
};

export interface MergePresenceOptions {
  /** When true, keep existing presence fields — do not default missing rows to offline. */
  presenceFailed?: boolean;
}

/** Relative for recent (< 24h), absolute otherwise — matches list mockup. */
export function formatPresenceTime(iso?: string | null, now = moment()): string {
  if (!iso) {
    return '—';
  }
  const time = moment(iso);
  if (!time.isValid()) {
    return '—';
  }
  if (now.diff(time, 'hours', true) < 24) {
    return time.from(now);
  }
  return time.format('DD.MM.YYYY HH:mm:ss');
}

export function mergeEmployeesWithPresence(
  employees: IEmployeesList[],
  presenceRows: IEmployeePresence[],
  options: MergePresenceOptions = {}
): IEmployeesList[] {
  const { presenceFailed = false } = options;

  if (presenceFailed) {
    return employees.map(employee => ({ ...employee }));
  }

  const byId = new Map(presenceRows.map(row => [String(row.userId), row]));

  return employees.map(employee => {
    const presence = byId.get(String(employee.id));
    if (!presence) {
      return {
        ...employee,
        presenceStatus: 'offline' as PresenceStatus,
        lastContact: null
      };
    }
    return {
      ...employee,
      presenceStatus: presence.presenceStatus,
      lastContact: presence.lastContact ?? null,
      lastPositionTime: presence.lastPositionTime ?? employee.lastPositionTime ?? null,
      lastPositionType: presence.lastPositionType ?? null,
      hasTag: presence.hasTag,
      deviceId: employee.deviceId ?? presence.tagNumber ?? undefined
    };
  });
}

export function filterEmployeesByPresenceStatus(
  employees: IEmployeesList[],
  presenceStatus?: PresenceStatus | string | null
): IEmployeesList[] {
  if (!presenceStatus) {
    return employees;
  }
  return employees.filter(employee => employee.presenceStatus === presenceStatus);
}

export function paginateList<T>(
  items: T[],
  pageIndex: number,
  pageSize: number
): {
  page: T[];
  pagesCount: number;
} {
  const total = items.length;
  const pagesCount = total === 0 ? 0 : Math.ceil(total / pageSize);
  const safePage = Math.max(0, Math.min(pageIndex, Math.max(pagesCount - 1, 0)));
  const start = safePage * pageSize;
  return {
    page: items.slice(start, start + pageSize),
    pagesCount
  };
}

/** Compare list filters ignoring pagination. */
export function sameEmployeeFilters(
  a?: Record<string, unknown> | null,
  b?: Record<string, unknown> | null
): boolean {
  if (!a || !b) {
    return false;
  }
  const keys = [
    'presenceStatus',
    'fullTextSearch',
    'departmentId',
    'device',
    'lastPosition',
    'roles',
    'posts',
    'includePreview'
  ];
  return keys.every(key => String(a[key] ?? '') === String(b[key] ?? ''));
}
